import { getApiClient } from '../../api';
import { AutoComplete } from 'primereact/autocomplete';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { Button } from 'primereact/button';
import './Search.css';
import ChevronLink from '../common/ChevronLink/ChevronLink';
import { URLKeys } from '../../Constants';
import { useCouncilPath, useMunicipalityId } from '../../hooks/useCouncilPath';

type Props = {
    open: boolean;
    setOpen: (open: boolean) => void;
};

const Search = ({ open, setOpen }: Props) => {
    const [value, setValue] = useState<string>('');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const { bodyType, zastupitelstvoId } = useParams();
    const municipalitaId = useMunicipalityId();
    const councilPath = useCouncilPath();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const autocompleteSearch = async (event: { query: string }) => {
        const api = getApiClient();
        const responseData = await api.getSearchByMunicipalityIdAndBodyAndCouncilId(
            municipalitaId,
            bodyType ?? '',
            parseInt(zastupitelstvoId ?? '0', 10),
            event.query ? { search: event.query } : undefined
        );

        const members = responseData.members || [];
        const parties = responseData.parties || [];
        const votings = responseData.votings || [];
        const membersLength = members.length;
        const partiesLength = parties.length;
        const votingsLength = votings.length;

        const MAX_ITEMS = 6;
        const ITEMS_PER_GROUP = 3;

        let membersCount = Math.min(ITEMS_PER_GROUP, membersLength);
        let partiesCount = Math.min(ITEMS_PER_GROUP, partiesLength);

        if (membersCount === 0 && partiesCount > 0) {
            partiesCount = Math.min(MAX_ITEMS, partiesLength);
        } else if (partiesCount === 0 && membersCount > 0) {
            membersCount = Math.min(MAX_ITEMS, membersLength);
        } else if (membersCount + partiesCount < MAX_ITEMS) {
            const remaining = MAX_ITEMS - (membersCount + partiesCount);
            if (membersLength > membersCount) {
                membersCount = Math.min(membersCount + remaining, membersLength);
            } else if (partiesLength > partiesCount) {
                partiesCount = Math.min(partiesCount + remaining, partiesLength);
            }
        }

        const newSuggestions = [];
        if (membersCount > 0) {
            newSuggestions.push({
                label: t('members', { ns: 'members' }),
                count: membersLength,
                items: [...members.slice(0, membersCount), { path: 'zastupitele' }]
            });
        }
        if (partiesCount > 0) {
            newSuggestions.push({
                label: t('parties', { ns: 'parties' }),
                count: partiesLength,
                items: [...parties.slice(0, partiesCount), { path: 'subjekty' }]
            });
        }
        if (votingsLength > 0) {
            newSuggestions.push({
                label: t('votings', { ns: 'votings' }),
                count: votingsLength,
                items: [{ path: `hlasovani?${URLKeys.voteDescription}=${event.query}#voting-cards` }]
            });
        }

        if (newSuggestions.length === 0) {
            newSuggestions.push({
                label: t('Search.searchNoResults'),
                items: []
            });
        }

        setSuggestions(newSuggestions);
    };

    const itemTemplate = (item: any) => {
        if (item?.path !== undefined) {
            return <ChevronLink chevronDirection='right' text={t('Search.displayAll')} linkDisabled />;
        }
        return (
            <div className='search-item'>
                {item?.party_name}
                {item?.jmeno} {item?.prijmeni}
                {item?.predmet}
            </div>
        );
    };

    const groupedItemTemplate = (item: any) => {
        return (
            <>
                <div>{item?.label}</div>
                {item?.count > 0 && <div>{t('totalResults', { totalCount: item?.count, count: item?.count })}</div>}
            </>
        );
    };

    const navigateToSelected = (item: any) => {
        if (item?.party_name !== undefined) {
            navigate(councilPath('subjekty', item?.party_id));
        }
        if (item?.predmet !== undefined) {
            navigate(councilPath('hlasovani', item?.id));
        }
        if (item?.jmeno !== undefined) {
            navigate(councilPath('zastupitele', item?.id));
        }
        if (item?.path !== undefined) {
            navigate(councilPath(item?.path));
        }
    };

    return (
        <div className='search-wrapper'>
            {open && (
                <AutoComplete
                    value={value}
                    suggestions={suggestions}
                    completeMethod={autocompleteSearch}
                    field='label'
                    optionGroupLabel='label'
                    optionGroupChildren='items'
                    optionGroupTemplate={groupedItemTemplate}
                    autoFocus
                    placeholder={t('Search.searchPlaceholder')}
                    onBlur={() => {
                        setTimeout(() => {
                            setOpen(false);
                            setValue('');
                        }, 200);
                    }}
                    itemTemplate={itemTemplate}
                    onChange={(e) => {
                        if (typeof e.value === 'object') {
                            navigateToSelected(e.value);
                            setOpen(false);
                            setValue('');
                        } else {
                            return setValue(e.value);
                        }
                    }}
                />
            )}
            {!open && (
                <Button
                    icon='pi pi-search'
                    className='p-button-rounded p-button-text'
                    aria-label='Search'
                    onClick={() => {
                        setOpen(!open);
                    }}
                />
            )}
        </div>
    );
};

export default Search;
